/**
 * Word Paste Extension for TipTap
 *
 * A TipTap extension that automatically cleans and converts
 * content pasted from Microsoft Word into proper semantic HTML.
 *
 * Features:
 * - Converts Word's fake lists to proper <ul>/<ol> elements
 * - Removes MSO-specific styles and classes
 * - Preserves bold, italic, underline, strikethrough formatting
 * - Cleans tables while preserving structure
 * - Removes conditional comments and Office namespace elements
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { cleanWordHTML, WordPasteCleanOptions, defaultCleanOptions } from "./htmlCleaner";

/**
 * Configuration options for the Word Paste extension
 */
export interface WordPasteOptions extends WordPasteCleanOptions {
  /**
   * Enable/disable the extension
   * @default true
   */
  enabled: boolean;
}

/**
 * Default options for the Word Paste extension
 */
const defaultOptions: WordPasteOptions = {
  ...defaultCleanOptions,
  enabled: true,
};

/**
 * Plugin key for the Word Paste extension
 */
export const wordPastePluginKey = new PluginKey("wordPaste");

/**
 * Word Paste Extension
 *
 * Automatically cleans Microsoft Word HTML when pasting into the editor.
 *
 * @example
 * ```typescript
 * import { WordPaste } from './extensions/word-paste';
 *
 * const editor = useEditor({
 *   extensions: [
 *     StarterKit,
 *     WordPaste.configure({
 *       preserveLists: true,
 *       preserveTables: true,
 *       preserveBold: true,
 *       preserveItalic: true,
 *     }),
 *   ],
 * });
 * ```
 */
export const WordPaste = Extension.create<WordPasteOptions>({
  name: "wordPaste",

  addOptions() {
    return defaultOptions;
  },

  addProseMirrorPlugins() {
    const options = this.options;

    return [
      new Plugin({
        key: wordPastePluginKey,
        props: {
          /**
           * Transform pasted HTML before it's parsed by ProseMirror
           * This is the main hook for cleaning Word HTML
           */
          transformPastedHTML(html: string): string {
            // Skip if extension is disabled
            if (!options.enabled) {
              return html;
            }

            // Clean the Word HTML using our cleaner
            return cleanWordHTML(html, {
              removeStyles: options.removeStyles,
              preserveBold: options.preserveBold,
              preserveItalic: options.preserveItalic,
              preserveUnderline: options.preserveUnderline,
              preserveStrikethrough: options.preserveStrikethrough,
              preserveLinks: options.preserveLinks,
              preserveImages: options.preserveImages,
              preserveTables: options.preserveTables,
              preserveLists: options.preserveLists,
              preserveHeadings: options.preserveHeadings,
            });
          },

          /**
           * Transform pasted text (plain text paste)
           * Word sometimes includes extra whitespace that should be cleaned
           */
          transformPastedText(text: string): string {
            if (!options.enabled) {
              return text;
            }

            // Clean up common Word text artifacts
            return text
              // Remove multiple spaces
              .replace(/  +/g, " ")
              // Remove Windows line endings
              .replace(/\r\n/g, "\n")
              // Remove multiple newlines (more than 2)
              .replace(/\n{3,}/g, "\n\n")
              .trim();
          },
        },
      }),
    ];
  },
});

export default WordPaste;
